<template>
  <v-dialog :value="value" persistent :width="multiple ? '1000' : '800'">
    <v-card>
      <v-card-title class="pl-5 pr-2 pt-4 pb-2">
        <v-icon class="mr-2" color="warning">mdi-alert</v-icon>
        <div class="title h6">
          {{ warningTitle }}
        </div>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="generateCSV">
          Export
          <v-icon class="pl-2 pt-0" size="large">
            mdi-download
          </v-icon>
        </v-btn>
        <v-card-subtitle class="py-2 pl-0 pr-2 ma-0" style="word-break: break-word">
          {{ warningText }}
        </v-card-subtitle>
      </v-card-title>
      <v-divider class="mx-4"/>
      <v-container fluid class="pa-0 mx-0">
        <v-data-table
          class="mx-4"
          :headers="headers"
          :items="promos"
          :group-by="multiple ? 'feature_pos' : undefined"
          :height="tableHeight"
          fixed-header
          dense
          :footer-props="{'items-per-page-options': [-1]}"
          :items-per-page="-1">
          <template v-if="multiple" #[`group.header`]="{ group, items, headers, toggle, isOpen }">
            <td :colspan="headers.length">
              <v-btn dense @click="toggle" small icon :ref="group" :data-open="isOpen">
                  <v-icon v-if="isOpen">mdi-chevron-up</v-icon>
                  <v-icon v-else>mdi-chevron-down</v-icon>
              </v-btn>
              <span>
                <b>
                  {{items[0].feature_pos}}
                </b>
              </span>
            </td>
          </template>
          <template v-slot:[`item.start_dt`]="{ item }">
            <span v-if="item.start_dt">
              {{ $dateConfig.formatDate(item.start_dt) }}</span>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.end_dt`]="{ item }">
            <span v-if="item.end_dt">
              {{ $dateConfig.formatDate(item.end_dt) }}</span>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.link`]="{ item }">
            <v-btn icon color="primary" @click="goToPromo(item.promo_id)">
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn> 
          </template>
        </v-data-table>
        <div class="d-flex justify-center">
          <v-btn class="mb-4 mr-4" text @click="close">
            Cancel
          </v-btn>
          <v-btn class="mb-4" color="warning" @click="confirm">
            Confirm
          </v-btn>
        </div>
      </v-container>
    </v-card>
  </v-dialog>
</template>
<script>
import { format } from '@/mixins/format'
import { buildUrl } from '@/mixins/build-url'
export default {
  data() {
    return {
      headerProps: {
        sortable: false,
        class: 'accent white--text'
      }
    }
  },
  name: 'FeaturePromoWarnings',
  mixins: [format, buildUrl],
  props: {
    value: Boolean,
    gappedPromos: Array,
    selectedAd: Object,
    move: Boolean,
    multiple: {
      type: Boolean,
      default: false
    },
    adClone: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    baseHeaders() {
      return [
        { text: 'Promo Name', value: 'promo_name' },
        { text: 'Ad Name', value: 'ad_name', show: this.multiple },
        { text: 'Feature Pos', value: 'feature_pos', show: this.multiple },
        { text: 'Promo Category', value: 'promo_category_name' },
        { text: 'Start Date', value: 'start_dt' },
        { text: 'End Date', value: 'end_dt' },
        { text: 'Allowances', value: 'allowances_formatted', width: '10%' },
        { text: 'Impact', value: 'impact' },
        { text: 'Original Promo', value: 'link', align: 'center' }
      ].filter(header => header.show === undefined || header.show)
    },
    headers() {
      return this.baseHeaders.map(header => {
        return { ...this.headerProps, ...header }
      })
    },
    newEntity() {
      return this.adClone ? 'ad' : this.multiple ? 'features' : 'feature'
    },
    warningTitle() {
      return `Promo Loss ${this.move ? '/ Adjustment' : '' } Warning`
    },
    warningText() {
      return `The following promotions do not overlap the target ad dates and will be ${this.move ? 'automatically adjusted by the system OR' : '' } dropped from the new ${this.newEntity}. Please review and confirm to proceed.`
    },
    promos() {
      return this.gappedPromos.map(promo => {
        return {
          ...promo,
          allowances_formatted: promo.allowances.map(a => `$${this.formatCurrencyDisplay(a)}`).join(' '),
          impact: (this.move && (['AD', 'PCAF'].includes(promo.billing_mode) && promo.promo_category_name !== 'DSD Off Invoice')) 
            ? 'System-driven promo adjustment (promo cloned or original modified)'
            : `Will not be included on the new ${this.newEntity}`
        }
      })
    },
    tableHeight() {
      return this.gappedPromos?.length >= 12 ? '384px' : undefined
    }
  },
  methods: {
    close () {
      this.$emit('cancel')
    },
    confirm () {
      this.$emit('confirmAction')
    },
    goToPromo(promo_id) {
      const link = this.buildPortalUrl('promo')
      window.open(`${link}/promo?id=${promo_id}`)
    },
    generateCSV () {
      const items = this.promos
      if (!items || items.length === 0) {
        return
      }

      const headers = this.baseHeaders.filter(h => h.value !== 'link').map(h => h.text)

      const sanitizeCSVField = (field) => {
        if (field === null || field === undefined) return '-'
        return `"${String(field).replace(/"/g, '""').replace(/[\n\r]+/g, ' ')}"`
      }

      const data = items.map(item => [
        sanitizeCSVField(item.promo_name),
        sanitizeCSVField(item.promo_category_name),
        sanitizeCSVField(item.start_dt),
        sanitizeCSVField(item.end_dt),
        sanitizeCSVField(item.allowances_formatted),
        sanitizeCSVField(item.impact)
      ])

      let csvContent = 'data:text/csv;charset=utf-8,'
      csvContent += [
        headers.join(','),
        ...data.map(e => e.join(','))
      ].join('\n')

      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)
      const adNameSafe = (this.selectedAd && this.selectedAd.ad_name)
        ? String(this.selectedAd.ad_name.toLowerCase()).replace(/[^a-z0-9_\-.]/gi, '_')
        : 'unknown-ad'
      link.setAttribute('download', `ad-${adNameSafe}-promo-warnings.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  }
}
</script>
<style>
.header {
  color: white;
  background: #385F73;
}
.title {
  width: 73%;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>